import styled, { css, keyframes } from "styled-components";

import { DATA_E2E_KEY, type IStyledAttributes } from "@ryde/shared";
import {
  deepPurpleMui,
  greyMui,
  rydeColors,
  ScreenDepth,
  touchTargetCss,
} from "@ryde/theme";

const placeInfoFadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const placeInfoPointPulse = keyframes`
  from {
    transform: scale(0.6);
    opacity: 0.9;
  }
  to {
    transform: scale(1.8);
    opacity: 0;
  }
`;

/**
 * The place-info button, a member of MapLibre's own control group, so it takes the zoom buttons'
 * size and chrome from MapLibre's stylesheet; '&&' outranks that sheet's display rule. On, it reads like
 * the passengers and stations map buttons: the icon turns purple and the button stays white.
 */
export const MapLibrePlaceInfoToggleStyled = styled.button.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "maplibre-place-info-toggle",
  },
)<{ $isActive: boolean }>`
  && {
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${({ $isActive }) =>
      $isActive ? deepPurpleMui.A700 : rydeColors.black};
  }

  & svg {
    width: 20px;
    height: 20px;
  }

  &:focus-visible {
    outline: 2px solid ${rydeColors.black};
    outline-offset: 2px;
  }
`;

/** The reminder that the map is in place-info mode, centred on top of it and never in a click's way. */
export const MapLibrePlaceInfoHintStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "maplibre-place-info-hint",
})`
  position: absolute;
  top: 10px;
  left: 0;
  right: 0;
  z-index: ${ScreenDepth.MAP_MAPLIBRE_LAYER_CONTROL};
  width: fit-content;
  max-width: calc(100% - 120px);
  margin: 0 auto;
  padding: 6px 12px;
  border-radius: 16px;
  background-color: ${greyMui._900};
  color: ${rydeColors.white};
  font-size: 12px;
  line-height: 1.4;
  text-align: center;
  pointer-events: none;

  @media (prefers-reduced-motion: no-preference) {
    animation: ${placeInfoFadeIn} 0.2s ease-out;
  }

  & .place-info-hint-exit {
    margin-inline-start: 6px;
    color: ${greyMui._400};
  }
`;

/** The clicked point, pulsing while it is looked up. */
export const MapLibrePlaceInfoPointStyled = styled.span.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "maplibre-place-info-point",
  },
)<{ $isLoading: boolean }>`
  position: relative;
  display: block;
  width: 14px;
  height: 14px;
  box-sizing: border-box;
  border: 2px solid ${rydeColors.white};
  border-radius: 50%;
  background-color: ${deepPurpleMui.A400};

  ${({ $isLoading }) =>
    $isLoading &&
    css`
      &::after {
        content: "";
        position: absolute;
        inset: -6px;
        border: 2px solid ${deepPurpleMui.A400};
        border-radius: 50%;
        opacity: 0;

        @media (prefers-reduced-motion: no-preference) {
          animation: ${placeInfoPointPulse} 1.2s ease-out infinite;
        }
      }
    `}
`;

/** The popup's card, laid out inside the shared popup chrome the map host gives every popup. */
export const MapLibrePlaceInfoCardStyled = styled.section.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "maplibre-place-info-card",
  },
)`
  width: 240px;
  max-width: 70vw;
  color: ${rydeColors.black};

  & .place-info-header {
    display: flex;
    align-items: flex-start;
    gap: 8px;
  }

  & .place-info-icon {
    flex: none;
    width: 20px;
    height: 20px;
    color: ${deepPurpleMui.A400};
  }

  & .place-info-texts {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  & .place-info-title {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.35;
    overflow-wrap: anywhere;
  }

  & .place-info-address,
  & .place-info-message {
    margin: 0;
    color: ${greyMui._700};
    overflow-wrap: anywhere;
  }

  & .place-info-coordinates {
    margin: 4px 0 0;
    font-size: 12px;
    color: ${greyMui._700};
    font-variant-numeric: tabular-nums;
    user-select: text;
  }

  & .place-info-retry {
    ${touchTargetCss};
    padding: 0;
    border: none;
    background: none;
    color: ${deepPurpleMui.A700};
    font: inherit;
    font-weight: 600;
    text-decoration: underline;
    cursor: pointer;

    &:focus-visible {
      outline: 2px solid ${rydeColors.black};
      outline-offset: 2px;
    }
  }

  & .place-info-actions {
    margin-top: 12px;
    padding-top: 10px;
    border-top: 1px solid ${greyMui._200};

    & .place-info-message {
      margin-top: 8px;
    }
  }

  & .place-info-add-station {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    width: 100%;
    min-height: 44px;
    padding: 6px 12px;
    border: none;
    border-radius: 8px;
    background-color: ${deepPurpleMui.A400};
    color: ${rydeColors.white};
    font: inherit;
    font-weight: 600;
    cursor: pointer;

    & svg {
      width: 18px;
      height: 18px;
    }

    &:hover:not(:disabled) {
      background-color: ${deepPurpleMui.A700};
    }

    &:focus-visible {
      outline: 2px solid ${rydeColors.black};
      outline-offset: 2px;
    }

    &:disabled {
      background-color: ${greyMui._400};
      cursor: default;
    }
  }
`;
